// src/components/pages/Pessoa/modules/usePessoaTabelaPrecoModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';
import * as DataListAPI from '../../../../api/dataList';
import * as TabelaAPI from '../../../../api/tabela';

function createTabelaPrecoDraft(pessoaId = null) {
    return {
        uuid: null,
        pessoaId: pessoaId || null,
        tabelaPrecoId: null,
        descricao: null,
        ativo: true,
        funcao: 'Adicionar',
    };
}

export function usePessoaTabelaPrecoModule({ pessoaId, empresaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createTabelaPrecoDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);
    const [tabelaPreco, setTabelaPreco] = useState({ id: null, descricao: null });
    const [optionsTabelaPreco, setOptionsTabelaPreco] = useState([]);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createTabelaPrecoDraft(pessoaId));
        setTabelaPreco({ id: null, descricao: null });
        setEditingIndex(null);
    }, [pessoaId]);

    const consultarTabelaPreco = useCallback(
        async (filtro) => {
            try {
                const result = await DataListAPI.dataList({
                    empresaId,
                    tabela: 'tabelaPreco',
                    filtro,
                });
                setOptionsTabelaPreco(result || []);
            } catch (error) {
                console.error(error);
            }
        },
        [empresaId]
    );

    const handleItem = useCallback(
        (row, index, funcao) => {
            const descricao = row?.tabelaPreco?.descricao || row?.descricao || null;

            setDraft({
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                tabelaPrecoId: row.tabelaPrecoId || null,
                descricao,
                ativo: !!row.ativo,
                funcao: funcao,
            });

            setTabelaPreco({
                id: row.tabelaPrecoId || null,
                descricao,
            });

            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                tabelaPrecoId: row.tabelaPrecoId || null,
                descricao: row?.tabelaPreco?.descricao || row?.descricao || null,
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Tabela de preço removida da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.tabelaPreco(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir tabela de preço.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.tabelaPrecoId) {
            setFlashMessage?.('Selecione a tabela de preço.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                let descricao = payload.descricao;

                if (!descricao && payload.tabelaPrecoId) {
                    try {
                        const d = await TabelaAPI.tabelaById(empresaId, 'tabelaPreco', payload.tabelaPrecoId);
                        descricao = d?.descricao || null;
                    } catch (error) {
                        console.error(error);
                    }
                }

                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                                  descricao,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Tabela de preço atualizada na lista.', 'success');
            } else {
                let descricao = payload.descricao;

                if (!descricao && payload.tabelaPrecoId) {
                    try {
                        const d = await TabelaAPI.tabelaById(empresaId, 'tabelaPreco', payload.tabelaPrecoId);
                        descricao = d?.descricao || null;
                    } catch (error) {
                        console.error(error);
                    }
                }

                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        descricao,
                        uuid: payload.uuid || `draft-tbp-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Tabela de preço adicionada na lista.', 'success');
            }

            resetDraft();
            return;
        }

        if (String(payload.funcao).toLowerCase() === 'deletar') {
            handleDelete(payload, payload.index);
            return;
        }

        try {
            const result = await PessoaAPI.tabelaPreco(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar tabela de preço.', 'error');
        }
    }, [draft, pessoaId, empresaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'tabelasPreco',
                header: 'Tabelas de preço da pessoa',
                columns: [
                    {
                        accessorFn: (row) => row?.tabelaPreco?.descricao || row?.descricao || '',
                        accessorKey: 'tabelaPreco.descricao',
                        header: 'Tabela de Preço',
                        size: 240,
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novaTabelaPreco: draft,
            tabelaPreco,
            optionsTabelaPreco,
        },
        actions: {
            setNovaTabelaPreco: setDraft,
            setTabelaPreco,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                handleItem(row, index, funcao);
            },
            change: async (e) => {
                const { name, value, checked } = e.target;

                if (name === 'novaTabelaPreco.descricao') {
                    setTabelaPreco((prev) => ({
                        ...prev,
                        descricao: value,
                    }));

                    await consultarTabelaPreco(value);

                    const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        tabelaPrecoId: selectedId ?? null,
                        descricao: value,
                    }));

                    return;
                }

                if (name === 'novaTabelaPreco.ativo') {
                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        ativo: checked,
                    }));
                }
            },
            focus: () => {},
            blur: () => {},
        },
        columns,
    };
}
